import { formatInTimeZone } from "date-fns-tz";

import { createCareplanPreviousProductDataModel } from "@/domains/careplan/factories/create-careplan-previous-product-data-model";
import { createCareplanProductDataModel } from "@/domains/careplan/factories/create-careplan-product-data-model";
import { createCareplanSubscriptionDataModel } from "@/domains/careplan/factories/create-careplan-subscription-data-model";
import { CAREPLAN_PRODUCT_ROLE } from "@/domains/careplan/models/constants";
import type {
  CareplanProductDataModel,
  CareplansResponse,
  PreviousCareplanProductDataModel,
  SubscriptionProductDataModel,
} from "@/domains/careplan/models/types";

export const createCareplanDataModel = (careplans: CareplansResponse) => {
  const currentProducts = careplans.current.products.map(createCareplanProductDataModel);
  const prevProducts =
    careplans.previous?.products.map(createCareplanPreviousProductDataModel) ?? [];
  const firstSubscription = careplans.current.subscriptions[0];
  const subscription = firstSubscription
    ? createCareplanSubscriptionDataModel(firstSubscription)
    : null;

  const noLongerRecommendedProducts = getNoLongerRecommendedProducts(currentProducts, prevProducts);

  const filteredSubscriptionProducts = subscription
    ? getFilteredSubscriptionProducts(subscription.products, noLongerRecommendedProducts)
    : [];

  const filteredSubscription = subscription
    ? { ...subscription, products: filteredSubscriptionProducts }
    : null;

  const cartProducts = filteredSubscription
    ? getDedupedCartProducts(currentProducts, filteredSubscription.products)
    : currentProducts;

  const cartVariantIds = cartProducts
    .filter((product) => {
      return product.variantId !== null;
    })
    .map((product) => {
      return `gid://shopify/ProductVariant/${product.variantId}`;
    });

  /*
    A first care plan has nothing to be "new" or "updated" against. Change tags only belong
    on a plan that replaced an earlier one, by a clinician or by a survey.
  */
  const isInitial = careplans.previous === null && careplans.current.version <= 1;

  return {
    id: careplans.current.id,
    version: careplans.current.version,
    isInitial,
    readAt: careplans.current.readAt,
    isRead: !!careplans.current.readAt,
    updatedAt: careplans.current.updatedAt
      ? getDisplayUpdatedAtDate(careplans.current.updatedAt)
      : null,
    wasBuiltFromSurvey: careplans.current.wasBuiltFromSurvey,
    progressTeaser: careplans.progressTeaser ?? null,
    progressEnabled: careplans.progressEnabled,
    subscription: filteredSubscription,
    currentProducts,
    productsWithHistory: getProductsGroupedByRole(currentProducts, prevProducts),
    cartProducts,
    cartVariantIds,
  };
};

export const getNoLongerRecommendedProducts = (
  currentProducts: CareplanProductDataModel[],
  prevProducts: PreviousCareplanProductDataModel[],
) => {
  const currentProductIds = new Set(
    currentProducts.map((product) => {
      return product.id;
    }),
  );

  return prevProducts.filter((product) => {
    return !currentProductIds.has(product.id);
  });
};

const getDedupedCartProducts = (
  currentProducts: CareplanProductDataModel[],
  subscriptionProducts: SubscriptionProductDataModel[],
) => {
  const subscriptionVariantIds = new Set(
    subscriptionProducts.map((product) => {
      return product.variantId;
    }),
  );

  return [
    ...subscriptionProducts,
    ...currentProducts.filter((product) => {
      return !product.variantId || !subscriptionVariantIds.has(product.variantId);
    }),
  ];
};

const getFilteredSubscriptionProducts = (
  subscriptionProducts: SubscriptionProductDataModel[],
  noLongerRecommendedProducts: PreviousCareplanProductDataModel[],
) => {
  const noLongerRecommendedVariantIds = new Set(
    noLongerRecommendedProducts
      .map((product) => {
        return product.variantId;
      })
      .filter((variantId): variantId is string => {
        return Boolean(variantId);
      }),
  );

  const filteredSubscriptionProducts = subscriptionProducts.filter((product) => {
    return !product.variantId || !noLongerRecommendedVariantIds.has(product.variantId);
  });

  return filteredSubscriptionProducts;
};

const getProductsGroupedByRole = (
  currentProducts: CareplanProductDataModel[],
  prevProducts: PreviousCareplanProductDataModel[],
) => {
  const currentProductIds = new Set(
    currentProducts.map((product) => {
      return product.id;
    }),
  );

  const dedupedPreviousProducts = prevProducts.filter((product) => {
    return !currentProductIds.has(product.id);
  });

  const allProducts = [...currentProducts, ...dedupedPreviousProducts];
  const rolePriority = Object.values(CAREPLAN_PRODUCT_ROLE);

  return rolePriority.flatMap((role) => {
    return allProducts.filter((product) => {
      return product.role === role;
    });
  });
};

const getDisplayUpdatedAtDate = (dateString: string) => {
  return formatInTimeZone(dateString, "UTC", "M/d/yy");
};
